/**
 * Service for handling audio playback functionality
 */

import store from '@/store';
import { isIOS } from '@/utils/platform';
import OfflineFxService from './OfflineFxService';
import {
  buildFxChain,
  dbToGain as fxDbToGain,
  calculateAutoMakeupGain as fxCalculateAutoMakeupGain
} from './fxChain';
import { envelopeValueAt } from './grEnvelope';
import { encodeWavBlob } from './wavEncoder';

// Every element event after which "what should the play/pause button show?"
// may have a different answer. All of them funnel into one re-evaluation of
// the element's actual state (see _syncPlaybackState).
const ELEMENT_STATE_EVENTS = [
  'play', 'playing', 'pause', 'waiting', 'stalled', 'ended', 'emptied',
  'error', 'abort', 'loadstart', 'loadedmetadata', 'loadeddata', 'canplay',
  'canplaythrough', 'seeking', 'seeked', 'timeupdate'
];

class AudioService {
  constructor() {
    this.audio = null;
    this.isPlaying = false;
    this.isLoading = true;
    this.trackEndedCallback = null;
    this.trackReloadCounter = 0;
    this.sameTrackPositionCounter = 0;
    this.previousTrackPosition = "00:00";

    // Web Audio API properties
    this.audioContext = null;
    this.sourceNode = null;
    this.sourceNodeAudioElement = null; // Track which audio element the source node is connected to
    this.eqFilters = [];
    this.compressor = null;
    this.gainNode = null;
    this.isWebAudioInitialized = false;

    // Background audio keep-alive
    this.audioContextKeepAliveInterval = null;
    this.hasStateChangeListener = false;

    // iOS Safari seeking workaround: track target position during seek
    // WebKit Bug #204228: currentTime is unreliable when connected to AudioContext
    this.pendingSeekPosition = null;
    this.pendingSeekTimeout = null;

    // Debounce rapid seeks to prevent audio glitches on rapid tapping
    this.lastSeekTime = 0;
    this.seekDebounceMs = 50; // Minimum time between seeks
    this.seekGainNode = null; // Dedicated GainNode for sample-accurate seek fading
    this.seekAnimationFrame = null; // Fallback rAF fade when Web Audio unavailable
    this._seekFadeHandler = null; // seeked event handler for fade-in scheduling
    this._seekFadeTimeout = null; // post-seeked settle / fallback timeout

    // iOS background audio: timeupdate-based end detection fallback
    this._nearEndTimeout = null;
    this._boundTimeupdateHandler = null;
    this._endDetectionFired = false;
    this._wrappedEndedCallback = null;

    // Next-track prefetch cache: stores blob URL keyed by original URL
    // iOS blocks network requests while the screen is locked, so we
    // pre-fetch the next track as a blob while the current one is playing.
    this._prefetchedUrl = null;    // original URL that was prefetched
    this._prefetchedBlobUrl = null; // blob URL ready for instant load
    this._prefetchAbortController = null;

    // Hybrid audio routing state (see HYBRID_AUDIO_ROUTING_PLAN.md).
    // iOS: FX are rendered offline to a WAV blob and hot-swapped into the
    // <audio> element; no realtime AudioContext is ever created on iOS.
    // Offline renders are deferred until the user has actually started
    // playback once — persisted FX settings must never trigger a full
    // fetch+decode+render at page load (a huge allocation on old iPhones,
    // possibly while CarPlay/navigation are already under memory pressure).
    this._hasUserInitiatedPlayback = false;
    this._fxTooLongNotifiedUrl = null; // snackbar shown once per track
    this._currentTrackUrl = null;      // original URL of the loaded track
    this._activeSwapBlobUrl = null;    // blob URL currently set as src via hot-swap
    this._pendingRevokeUrls = [];      // blob URLs to revoke once they are no longer the src
    this._playConfirmHandler = null;   // 'playing' listener confirming a fire-and-forget play()
    this._playConfirmTimeout = null;   // self-heal timer when play() never settles
    this._offlineRenderTimer = null;   // debounce timer for offline re-renders
    this._fxSubscriptionRegistered = false;
    this._swapInProgress = false;
    this._reductionEnvelope = null;    // estimated GR envelope for the iOS meter

    // Seamless (gapless) FX switching: a second audio element plays the
    // rendered version muted, gets clock-synced to the live element, then
    // the two flip roles via muted-toggle — no audible interruption.
    // (A volume crossfade is impossible on iPhone: volume is user-only.)
    this._standbyAudio = null;
    this._standbyUnlocked = false;     // element blessed by a user gesture
    this._swapGen = 0;                 // bumped on track change to abort in-flight swaps
    this._queuedSwap = null;           // latest swap requested while one was running
    this._swapStartedAt = 0;           // for stale-lock self-healing
    this._staleSwapThresholdMs = 15000; // force-clear a stuck swap after this
    this._seamlessPlayTimeoutMs = 3000; // iOS can leave play() pending forever
    this._flipVerifyDelayMs = 600;     // post-flip "did it really take" check

    // Playback state published to the store (single writer, see
    // _syncPlaybackState): `isPlaying` is the transport intent — "audio
    // should be playing" — and the button state is derived from that plus
    // what the ACTIVE ELEMENT is really doing. The element's own events keep
    // both honest, so an OS interruption, a lock-screen tap or a play() that
    // never settles can no longer strand the button in the wrong state.
    this._onElementStateEvent = (event) => this._handleElementStateEvent(event);
    this._stateBoundElement = null;   // element the state listeners are attached to
    this._transitionDepth = 0;        // >0 while an internal reload pauses the element on purpose
    this._publishedPlaybackState = null;
    this._endedSelfHealTimer = null;

    // Initialize AudioContext on first user interaction (required for Chrome)
    this.initAudioContextOnInteraction();

    // iOS: pre-unlock the standby element for seamless FX switching
    this.setupStandbyUnlockOnInteraction();

    // Set up background audio support
    this.setupBackgroundAudioSupport();
  }

  /**
   * iOS grants an element the right to play() programmatically only after it
   * has played during a user gesture. Bless the standby element with a
   * silent clip on the first interaction so seamless FX switches (which
   * happen seconds after the triggering tap) can start and unmute it later.
   */
  setupStandbyUnlockOnInteraction() {
    if (!isIOS() || typeof document === 'undefined') {
      return;
    }

    const unlock = () => {
      document.removeEventListener('touchstart', unlock, true);
      document.removeEventListener('click', unlock, true);
      try {
        if (!this._standbyAudio) {
          this._standbyAudio = new Audio();
          this._standbyAudio.crossOrigin = 'anonymous';
        }
        const el = this._standbyAudio;

        // 100ms of real silence (a 0-length clip can put the element in an
        // error state on iOS, which would void the gesture blessing)
        const silentBlob = encodeWavBlob({
          numberOfChannels: 1,
          sampleRate: 8000,
          length: 800,
          getChannelData: () => new Float32Array(800)
        });
        const silentUrl = URL.createObjectURL(silentBlob);

        el.src = silentUrl;
        const p = el.play();
        if (p && typeof p.then === 'function') {
          p.then(() => {
            el.pause();
            this._standbyUnlocked = true;
            console.info('[FX] standby element unlocked for seamless switching');
            URL.revokeObjectURL(silentUrl);
          }).catch((e) => {
            console.info('[FX] standby unlock play refused:', e && e.name);
            URL.revokeObjectURL(silentUrl);
          });
        } else {
          this._standbyUnlocked = true;
        }
      } catch (e) {
        console.info('[FX] standby unlock failed:', e && e.message);
        // Seamless switching will fall back to the instant swap
      }
    };

    document.addEventListener('touchstart', unlock, { capture: true });
    document.addEventListener('click', unlock, { capture: true });
  }

  /**
   * Ensure a persistent Audio element exists.
   * Creates one on first call; returns the existing one on subsequent calls.
   * Reusing the same element across tracks preserves iOS Safari's
   * user-gesture activation for background playback.
   */
  _ensureAudioElement() {
    if (!this.audio) {
      this.audio = new Audio();
      this.audio.crossOrigin = "anonymous";

      // Attach the ended callback once, permanently
      if (this.trackEndedCallback) {
        this._attachEndedListener(this.trackEndedCallback);
      }

      // Set up timeupdate-based end detection for iOS background
      this._setupTimeupdateEndDetection();

      // The element's events are what the play/pause button is derived from
      this._bindElementStateListeners(this.audio);
    }
    return this.audio;
  }

  /**
   * Attach the ended listener with end-detection coordination.
   * Wraps the callback to set _endDetectionFired so the timeupdate
   * fallback does not double-fire.
   */
  _attachEndedListener(callback) {
    this._wrappedEndedCallback = () => {
      // The pre-end advance (or the timeupdate fallback) may have already
      // handled this track boundary — never advance twice.
      if (this._endDetectionFired) {
        return;
      }
      this._endDetectionFired = true;
      if (this._nearEndTimeout) {
        clearTimeout(this._nearEndTimeout);
        this._nearEndTimeout = null;
      }
      callback();
    };
    this.audio.addEventListener("ended", this._wrappedEndedCallback);
  }

  /**
   * Set up timeupdate-based end detection as a fallback for iOS Safari.
   * iOS may suspend the web process before the 'ended' event fires.
   * This monitors timeupdate events and triggers the ended callback
   * if playback reaches near the end and then stops advancing.
   */
  _setupTimeupdateEndDetection() {
    if (this._boundTimeupdateHandler) return;

    this._boundTimeupdateHandler = () => {
      if (!this.audio || !this.isPlaying) return;

      const remaining = this.audio.duration - this.audio.currentTime;

      // PRE-END ADVANCE (iOS, screen dark): while audio is playing the web
      // process is alive and timeupdate keeps firing — but the moment the
      // track ENDS, iOS may suspend the page before 'ended' runs, or leave
      // the ended-handler's play() pending forever. So with the page hidden
      // we advance ~0.4s BEFORE the end, while the pipeline is still hot:
      // the synchronous src-swap + play() reads as continuous playback.
      // (Foreground keeps the normal, gapless 'ended' flow — it is proven
      // reliable there, and no tail gets trimmed.)
      if (
        isIOS() &&
        typeof document !== 'undefined' &&
        document.hidden &&
        isFinite(remaining) &&
        remaining > 0 &&
        remaining <= AudioService.PRE_END_ADVANCE_SECONDS &&
        !this._endDetectionFired &&
        this.trackEndedCallback
      ) {
        this._endDetectionFired = true;
        if (this._nearEndTimeout) {
          clearTimeout(this._nearEndTimeout);
          this._nearEndTimeout = null;
        }
        this.trackEndedCallback();
        return;
      }

      if (this._nearEndTimeout) {
        clearTimeout(this._nearEndTimeout);
        this._nearEndTimeout = null;
      }

      // If within 2 seconds of the end, set a 3-second timeout.
      // timeupdate fires roughly every 250ms during playback.
      // If 3 seconds pass without another timeupdate (process suspended),
      // trigger the ended callback.
      if (remaining > 0 && remaining <= 2 && !this._endDetectionFired) {
        this._nearEndTimeout = setTimeout(() => {
          if (this.audio && this.isPlaying && !this._endDetectionFired) {
            const currentRemaining = this.audio.duration - this.audio.currentTime;
            if (currentRemaining <= 2) {
              this._endDetectionFired = true;
              if (this.trackEndedCallback) {
                this.trackEndedCallback();
              }
            }
          }
          this._nearEndTimeout = null;
        }, 3000);
      }
    };

    this.audio.addEventListener("timeupdate", this._boundTimeupdateHandler);
  }

  /**
   * Initialize AudioContext on first user interaction
   * Required for Chrome's autoplay policy - must happen during user gesture
   */
  initAudioContextOnInteraction() {
    // iOS: never create a realtime AudioContext. iOS treats pages with a live
    // AudioContext as "interactive audio" and interrupts them aggressively on
    // CarPlay/Bluetooth routes and screen lock. FX use offline rendering instead.
    if (isIOS()) {
      return;
    }

    const initContext = async () => {
      if (!this.audioContext) {
        try {
          this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

          // Resume immediately if suspended - MUST await to ensure it completes
          if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
          }

          // Set up state change listener immediately after creating AudioContext
          this.setupAudioContextStateChangeListener();
        } catch (error) {
          console.error('Error creating AudioContext:', error);
        }
      }

      // Remove listeners after first interaction
      document.removeEventListener('click', initContext);
      document.removeEventListener('touchstart', initContext);
      document.removeEventListener('keydown', initContext);
    };

    // Listen for first user interaction
    document.addEventListener('click', initContext, { capture: true });
    document.addEventListener('touchstart', initContext, { capture: true });
    document.addEventListener('keydown', initContext, { capture: true });
  }

  /**
   * Set up background audio support for mobile devices
   * Handles page visibility changes and keeps AudioContext alive
   */
  setupBackgroundAudioSupport() {
    // Tell iOS 17.5+ that this is a background audio playback app.
    // Prevents AudioContext from being suspended when the page is hidden.
    if ('audioSession' in navigator) {
      navigator.audioSession.type = 'playback';
    }

    // Handle visibility changes - resume AudioContext when page becomes hidden
    // This is critical for mobile background playback
    document.addEventListener('visibilitychange', async () => {
      if (this.audioContext && this.isPlaying) {
        // PROACTIVE RESUME: On iOS, try to resume IMMEDIATELY on visibility change,
        // even if state is still 'running'. This can help minimize the gap.
        if (document.hidden) {
          // Page going to background - proactively resume to minimize interruption gap
          this.audioContext.resume().catch(() => {});
        } else {
          // Page coming to foreground - resume if suspended or interrupted
          if (this.audioContext.state !== 'running') {
            this.audioContext.resume().catch(() => {});
          }
        }
      }
    });

    // Handle page lifecycle events
    document.addEventListener('freeze', () => {
      if (this.audioContext && this.isPlaying) {
        this.audioContext.resume().catch(() => {});
      }
    }, { capture: true });

    document.addEventListener('resume', () => {
      if (this.audioContext && this.isPlaying && this.audioContext.state !== 'running') {
        this.audioContext.resume().catch(() => {});
      }
    }, { capture: true });

    // Handle page blur/focus
    window.addEventListener('blur', () => {
      if (this.audioContext && this.isPlaying) {
        // Proactive resume on blur
        this.audioContext.resume().catch(() => {});
      }
    });

    window.addEventListener('focus', () => {
      if (this.audioContext && this.isPlaying && this.audioContext.state !== 'running') {
        this.audioContext.resume().catch(() => {});
      }
    });

    // Whenever the page comes back into view, trust the element over our
    // flags: a pause that happened while the page was frozen (iOS dropping
    // the audio session of a background tab, say) may never have been
    // delivered as an event.
    const reconcile = () => this._reconcileWithElement();
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) reconcile();
    });
    window.addEventListener('pageshow', reconcile);
    window.addEventListener('focus', reconcile);

    // Set up Media Session API for OS-level media controls integration
    this.setupMediaSession();
  }

  /**
   * Set up listener for AudioContext state changes
   * This catches the "interrupted" state immediately on iOS
   * NOTE: We only LOG here, not resume - iOS doesn't allow resume() without user gesture
   */
  setupAudioContextStateChangeListener() {
    if (!this.audioContext || this.hasStateChangeListener) return;

    // Listen for state changes on the AudioContext
    // On iOS, this fires when AudioContext goes to "interrupted" state (screen lock)
    this.audioContext.addEventListener('statechange', () => {
      // DON'T try to resume here - iOS blocks resume() in statechange handler
      // The keep-alive mechanism will handle resuming
    });

    this.hasStateChangeListener = true;
  }

  /**
   * Start keep-alive mechanism for AudioContext
   * Periodically checks and resumes AudioContext to prevent iOS from suspending it
   */
  startAudioContextKeepAlive() {
    // Clear any existing interval
    this.stopAudioContextKeepAlive();

    // No realtime AudioContext (iOS / native mode) -> nothing to keep alive
    if (!this.audioContext) {
      return;
    }

    // Check every 50ms to catch interruptions as quickly as possible
    // This is the mechanism that resumes the AudioContext after iOS interrupts it
    // Trade-off: 50ms interval uses slightly more CPU but minimizes audio gap on iOS
    this.audioContextKeepAliveInterval = setInterval(() => {
      if (this.audioContext && this.isPlaying) {
        if (this.audioContext.state !== 'running') {
          this.audioContext.resume().catch(() => {});
        }
      }
    }, 50); // Check every 50ms to minimize audio gap on iOS screen lock
  }

  /**
   * Stop keep-alive mechanism for AudioContext
   */
  stopAudioContextKeepAlive() {
    if (this.audioContextKeepAliveInterval) {
      clearInterval(this.audioContextKeepAliveInterval);
      this.audioContextKeepAliveInterval = null;
    }
  }

  /**
   * Set up Media Session API to integrate with OS media controls
   * This tells the OS that this is a media playback app
   */
  setupMediaSession() {
    if ('mediaSession' in navigator && 'MediaMetadata' in window) {
      // Set default metadata (will be updated when track loads)
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: 'Platinum Couch Productions',
        artist: 'Loading...',
        album: 'Audio Player',
        artwork: []
      });

      // Set up action handlers for media controls.
      // IMPORTANT: these consult the ELEMENT's state, never this.isPlaying —
      // a play() promise iOS left pending can strand isPlaying=true, and the
      // old `if (!this.isPlaying)` guard then turned the lock-screen /
      // CarPlay play button into a dead no-op.
      const actionHandlers = {
        play: () => {
          this._resumeFromMediaSession();
        },
        pause: () => {
          if (this.audio && !this.audio.paused) {
            this.pause();
          } else {
            // Element already stopped — just heal the flags/OS/UI state
            this.stop();
          }
        },
        previoustrack: () => {
          // Dispatch to Vuex store
          store.dispatch('player/previousTrack');
        },
        nexttrack: () => {
          // Dispatch to Vuex store
          store.dispatch('player/nextTrack');
        },
        seekto: (details) => {
          if (this.audio && details.seekTime) {
            const seekPercent = (details.seekTime / this.audio.duration) * 100;
            this.seek(seekPercent);
          }
        }
      };

      // Register each action handler
      for (const [action, handler] of Object.entries(actionHandlers)) {
        try {
          navigator.mediaSession.setActionHandler(action, handler);
        } catch (error) {
          // Action not supported
        }
      }
    }
  }

  /**
   * Resurrect playback from a lock-screen / CarPlay play command.
   * Must work no matter what state a failed background advance left behind:
   * stale isPlaying flag, a play() promise that never settled, or an
   * element whose load errored. The element is the source of truth.
   */
  _resumeFromMediaSession() {
    const audio = this.audio;
    if (!audio) return;

    // Genuinely playing already — just heal the flags
    if (!audio.paused && !audio.ended) {
      this.isPlaying = true;
      this._syncPlaybackState();
      return;
    }

    // If the element is in an error state or has no usable source, reload
    // the current track from its original URL before trying to play.
    if ((audio.error || !audio.src) && this._currentTrackUrl) {
      try {
        audio.src = this._currentTrackUrl;
        audio.load();
      } catch (e) {
        // fall through and attempt play() anyway
      }
    }

    audio.volume = 1;
    // Transport on from this moment (the UI shows "pause"/spinner); the
    // confirmation listener self-heals the flags if play() never settles.
    this.isPlaying = true;
    this._armPlaybackConfirmation();
    this._syncPlaybackState();

    const playPromise = audio.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(() => {
          this.isPlaying = true;
          this.startAudioContextKeepAlive();
          this._syncPlaybackState();
        })
        .catch(() => {
          this._markPlaybackFailed();
        });
    }
  }

  /**
   * Confirm that a fire-and-forget play() actually started playback, and
   * self-heal isPlaying / Media Session state if it never does. iOS can
   * leave play() promises pending forever; without this, a stale
   * isPlaying=true silently disabled every later recovery path.
   */
  _armPlaybackConfirmation() {
    const audio = this.audio;
    if (!audio) return;

    this._disarmPlaybackConfirmation();

    const confirmed = () => {
      this._disarmPlaybackConfirmation();
      this.isPlaying = true;
      this._syncPlaybackState();
    };
    this._playConfirmHandler = confirmed;
    audio.addEventListener('playing', confirmed);

    // If the element still hasn't started after the grace period, mark the
    // attempt failed so lock-screen play / UI reflect reality. (While the
    // page is suspended this timer is frozen — it then fires on wake, which
    // is exactly when the healed state is needed.)
    this._playConfirmTimeout = setTimeout(() => {
      this._playConfirmTimeout = null;
      if (this.audio && this.audio.paused) {
        this._markPlaybackFailed();
      }
    }, AudioService.PLAY_CONFIRM_TIMEOUT_MS);
  }

  _disarmPlaybackConfirmation() {
    if (this._playConfirmHandler && this.audio) {
      this.audio.removeEventListener('playing', this._playConfirmHandler);
    }
    this._playConfirmHandler = null;
    if (this._playConfirmTimeout) {
      clearTimeout(this._playConfirmTimeout);
      this._playConfirmTimeout = null;
    }
  }

  _markPlaybackFailed() {
    this._disarmPlaybackConfirmation();
    // Inside an internal reload (track change, FX hot-swap) the interrupted
    // play() is expected — the reload issues its own play() when done.
    if (this._isInternalTransition()) {
      return;
    }
    this.isPlaying = false;
    this.stopAudioContextKeepAlive();
    this._syncPlaybackState();
  }

  // ---------------------------------------------------------------------
  // Playback state: the element is the source of truth
  // ---------------------------------------------------------------------

  /**
   * Attach the state listeners to the element the UI should reflect. Called
   * for the persistent element and again whenever the seamless FX switch
   * makes the standby element the active one.
   * @param {HTMLAudioElement} element
   */
  _bindElementStateListeners(element) {
    if (!element || this._stateBoundElement === element) {
      return;
    }
    this._unbindElementStateListeners();
    for (const type of ELEMENT_STATE_EVENTS) {
      element.addEventListener(type, this._onElementStateEvent);
    }
    this._stateBoundElement = element;
  }

  _unbindElementStateListeners() {
    const element = this._stateBoundElement;
    if (!element) {
      return;
    }
    for (const type of ELEMENT_STATE_EVENTS) {
      element.removeEventListener(type, this._onElementStateEvent);
    }
    this._stateBoundElement = null;
  }

  /**
   * One handler for every element event that can change what the button
   * should show. Most events only trigger a re-evaluation; a few carry
   * meaning for the transport intent itself.
   * @param {Event} event
   */
  _handleElementStateEvent(event) {
    const element = this.audio;
    if (!element) {
      return;
    }
    // A retired element (seamless FX swap) can still deliver queued events
    if (event && event.currentTarget && event.currentTarget !== element) {
      return;
    }

    const type = event ? event.type : '';
    if (type === 'pause') {
      // Our own pause()/fadeOutAndPause() already turned the intent off. A
      // pause we did NOT ask for is another app taking the audio session,
      // headphones unplugging, a phone call, the OS media controls with no
      // action handler... → the transport is off and the button must show
      // "play". Not counted as external: the pauses an internal reload
      // performs (loadTrack / FX hot-swap) and the pause the browser does
      // right before firing 'ended' (the ended handler decides what's next).
      if (
        this.isPlaying &&
        element.paused &&
        !this._isInternalTransition() &&
        !this._hasReachedEnd(element)
      ) {
        this._markExternallyPaused('pause event');
        return;
      }
    } else if (type === 'play' || type === 'playing') {
      // The element is running although the transport says off: it was
      // started without going through play() (iOS auto-resuming after an
      // interruption ends), or a play() we had given up on finally took.
      // Audio is audibly playing → the transport is on.
      if (!this.isPlaying && !element.paused) {
        this.isPlaying = true;
        this.startAudioContextKeepAlive();
      }
    } else if (type === 'ended') {
      this._scheduleEndedSelfHeal(element);
    }

    this._syncPlaybackState();
  }

  /** True while a reload of ours may pause/restart the element on purpose. */
  _isInternalTransition() {
    return this._transitionDepth > 0 || this._swapInProgress;
  }

  /**
   * Whether the element sits at the end of its track. Browsers pause the
   * element (firing 'pause') right before 'ended' — that pause is the end
   * of the track, not an interruption.
   * @param {HTMLAudioElement} element
   */
  _hasReachedEnd(element) {
    if (element.ended) {
      return true;
    }
    const duration = element.duration;
    return (
      isFinite(duration) &&
      duration > 0 &&
      duration - element.currentTime <= AudioService.END_OF_TRACK_EPSILON_SECONDS
    );
  }

  /**
   * Playback stopped without us asking for it: turn the transport off so
   * the UI, the Media Session and the stall monitor all agree with the
   * element.
   * @param {string} reason - for the console only
   */
  _markExternallyPaused(reason) {
    this.isPlaying = false;
    this._disarmPlaybackConfirmation();
    this.stopAudioContextKeepAlive();
    console.info('[playback] paused outside the app (' + reason + ')');
    this._syncPlaybackState();
  }

  /**
   * After 'ended', something must take over (next track, repeat, or an
   * explicit stop at the end of the playlist). If nothing did, the element
   * sits paused at its end while the intent still says "playing" — heal.
   * @param {HTMLAudioElement} element
   */
  _scheduleEndedSelfHeal(element) {
    if (this._endedSelfHealTimer) {
      clearTimeout(this._endedSelfHealTimer);
    }
    this._endedSelfHealTimer = setTimeout(() => {
      this._endedSelfHealTimer = null;
      if (this.audio === element && this.isPlaying && element.paused && element.ended) {
        this._markExternallyPaused('track ended, nothing followed');
      }
    }, AudioService.ENDED_SELF_HEAL_MS);
  }

  /**
   * Trust the element over our flags whenever we get a chance to look
   * (page becomes visible, periodic monitor tick). Catches a pause that was
   * never delivered as an event because the page was frozen at the time.
   */
  _reconcileWithElement() {
    const element = this.audio;
    if (
      element &&
      this.isPlaying &&
      element.paused &&
      !element.ended &&
      !this._isInternalTransition() &&
      !this._playConfirmHandler // a play() is still pending; its confirmation settles it
    ) {
      this._markExternallyPaused('element found paused');
      return;
    }
    this._syncPlaybackState();
  }

  /**
   * Derive what the play/pause button should show and publish it — the ONLY
   * place that writes the store's playback state and the Media Session's
   * playbackState:
   *   - paused:  the transport is off (user, OS, failure, end of playlist)
   *   - playing: the transport is on and the element is actually producing
   *              audio (not paused, has data to play)
   *   - loading: the transport is on but audio isn't flowing yet (track
   *              fetch, buffering, a play() still settling)
   * Idempotent: commits only when the derived state changes.
   */
  _syncPlaybackState() {
    const element = this.audio;
    const isPlaying = !!this.isPlaying;
    const rendering =
      !!element &&
      !element.paused &&
      !element.ended &&
      !element.error &&
      // Unknown readyState (exotic hosts) counts as ready
      (typeof element.readyState !== 'number' ||
        element.readyState >= AudioService.HAVE_FUTURE_DATA);
    const state = !isPlaying ? 'paused' : rendering ? 'playing' : 'loading';

    if (state === this._publishedPlaybackState) {
      return;
    }
    this._publishedPlaybackState = state;

    try {
      store.commit('player/SET_PLAYBACK_STATE', {
        isPlaying,
        isLoading: state === 'loading'
      });
    } catch (e) {
      // Store not available (module initialization) — nothing to mirror yet
    }

    if (typeof navigator !== 'undefined' && 'mediaSession' in navigator) {
      try {
        navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
      } catch (e) {
        // Media Session may reject state updates in some contexts
      }
    }
  }

  /**
   * Transport off without touching the element (end of the playlist, where
   * the browser has already paused it; Media Session pause on a stopped
   * element).
   */
  stop() {
    this.isPlaying = false;
    this._disarmPlaybackConfirmation();
    this.stopAudioContextKeepAlive();
    this._syncPlaybackState();
  }

  /**
   * The store is about to load a different track and play it: turn the
   * transport on right away so the button shows the spinner (then "pause")
   * while the new track loads, instead of a play icon that would suggest
   * nothing is happening. loadTrack() masks the element pauses the reload
   * performs; play() settles the rest.
   */
  beginTrackChange() {
    this.isPlaying = true;
    this._syncPlaybackState();
  }

  /**
   * Update Media Session metadata with current track info
   * @param {Object} trackInfo - Track information
   */
  updateMediaSessionMetadata(trackInfo) {
    if ('mediaSession' in navigator && 'MediaMetadata' in window && trackInfo) {
      try {
        navigator.mediaSession.metadata = new window.MediaMetadata({
          title: trackInfo.name || 'Unknown Track',
          artist: 'Platinum Couch Productions',
          album: trackInfo.date ? new Date(trackInfo.date).getFullYear().toString() : 'Unknown Year',
          artwork: [
            { src: '/img/logo.png', sizes: '512x512', type: 'image/png' }
          ]
        });

        // Update playback state
        navigator.mediaSession.playbackState = this.isPlaying ? 'playing' : 'paused';
      } catch (error) {
        // Could not update Media Session metadata
      }
    }
  }

  /**
   * Initialize Web Audio API and connect audio processing graph
   * Note: This requires CORS to be enabled on the audio source
   */
  initializeWebAudio() {
    if (!this.audio) {
      return;
    }

    // iOS: never capture the element with createMediaElementSource — FX are
    // applied via offline rendering so playback stays on the native pipeline
    // (createMediaElementSource cannot be undone once called).
    if (isIOS()) {
      return;
    }

    // If already initialized, we need to reconnect to the new audio element
    if (this.isWebAudioInitialized) {
      this.reconnectWebAudio();
      return;
    }

    try {
      // Note: crossOrigin must be set before loading the audio
      // If audio is already loaded, this won't work and we need to reload
      // Create audio context (only once)
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      }

      // Create source from HTML5 audio element
      this.sourceNode = this.audioContext.createMediaElementSource(this.audio);
      this.sourceNodeAudioElement = this.audio; // Track which audio element we're connected to

      // Build the FX chain through the shared builder (fxChain.js) so the
      // realtime graph and the iOS offline renderer can never drift apart.
      const chain = buildFxChain(this.audioContext, this._getFxSettingsSnapshot());
      this.eqFilters = chain.eqFilters;
      this.compressor = chain.compressor;
      this.gainNode = chain.gainNode;

      // Dedicated gain node for sample-accurate seek fading (always unity gain unless seeking)
      this.seekGainNode = this.audioContext.createGain();
      this.seekGainNode.gain.value = 1;

      // Connect the audio graph:
      // source -> eq1..eq5 -> compressor -> gain -> seekGain -> destination
      this.sourceNode.connect(chain.input);
      chain.output.connect(this.seekGainNode);
      this.seekGainNode.connect(this.audioContext.destination);

      this.isWebAudioInitialized = true;

      // CRITICAL FIX: Ensure AudioContext is running after initialization
      // On iOS, if the AudioContext was created earlier (e.g., when FX modal opened),
      // it might be suspended. We need to ensure it's running during this user gesture.
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume().catch(() => {});
      }

      // Set up listener for AudioContext state changes (iOS-specific handling)
      this.setupAudioContextStateChangeListener();
    } catch (error) {
      console.error('Error initializing Web Audio API:', error);
      this.isWebAudioInitialized = false;
    }
  }

  /**
   * Reconnect Web Audio API to a new audio element
   * Called when the audio element changes but Web Audio is already initialized
   */
  reconnectWebAudio() {
    if (!this.audio || !this.audioContext) {
      return;
    }

    // Check if we're already connected to the current audio element
    if (this.audio === this.sourceNodeAudioElement) {
      return;
    }

    try {

      // Disconnect the old source node if it exists
      if (this.sourceNode) {
        try {
          this.sourceNode.disconnect();
        } catch (e) {
          // Source might already be disconnected, ignore error
        }
      }

      // Create new source from the new audio element
      this.sourceNode = this.audioContext.createMediaElementSource(this.audio);
      this.sourceNodeAudioElement = this.audio; // Track the new audio element

      // Reconnect to the existing filter chain
      this.sourceNode.connect(this.eqFilters[0]);
    } catch (error) {
      console.error('Error reconnecting Web Audio API:', error);
    }
  }

  /**
   * Create a BiquadFilter node
   * @param {string} type - Filter type (lowshelf, peaking, highshelf)
   * @param {number} frequency - Frequency in Hz
   * @param {number} gain - Gain in dB
   * @param {number} q - Q factor
   * @returns {BiquadFilterNode} - The created filter node
   */
  createFilter(type, frequency, gain, q) {
    const filter = this.audioContext.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = frequency;
    filter.gain.value = gain;
    filter.Q.value = q;
    return filter;
  }

  /**
   * Ensure Web Audio API is initialized when effects are changed
   * This allows lazy initialization - Web Audio is only set up when the user actually uses effects
   * @returns {boolean} - True if Web Audio is initialized and ready
   */
  ensureWebAudioInitialized() {
    // iOS never uses the realtime graph — FX changes are picked up by the
    // store subscription and applied via offline rendering instead.
    if (isIOS()) {
      return false;
    }

    // Can't initialize without an audio element
    if (!this.audio) {
      return false;
    }

    // Already initialized
    if (this.isWebAudioInitialized) {
      return true;
    }

    // Create AudioContext if needed
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }

    // Resume if suspended (should be during user gesture since they're interacting with effects UI)
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch(() => {});
    }

    // Initialize the Web Audio graph
    this.initializeWebAudio();

    // Start keep-alive if currently playing
    if (this.isPlaying) {
      this.startAudioContextKeepAlive();
    }

    return this.isWebAudioInitialized;
  }

  /**
   * Update an EQ band
   * @param {number} index - Filter index (0-4)
   * @param {number} frequency - Frequency in Hz
   * @param {number} gain - Gain in dB
   * @param {number} q - Q factor
   */
  updateEqBand(index, frequency, gain, q) {
    // Lazily initialize Web Audio when user changes effects
    this.ensureWebAudioInitialized();

    if (this.isWebAudioInitialized && this.eqFilters[index] && this.audioContext) {
      // Use smooth transitions for gain to avoid clicks (10ms for responsive slider feel)
      this.eqFilters[index].frequency.value = frequency;
      this.eqFilters[index].gain.setTargetAtTime(gain, this.audioContext.currentTime, 0.01);
      this.eqFilters[index].Q.value = q;
    }
  }

  /**
   * Update compressor parameters
   * @param {number} threshold - Threshold in dB
   * @param {number} ratio - Compression ratio
   * @param {number} attack - Attack time in ms
   * @param {number} release - Release time in ms
   */
  updateCompressor(threshold, ratio, attack, release) {
    // Lazily initialize Web Audio when user changes effects
    this.ensureWebAudioInitialized();

    if (this.isWebAudioInitialized && this.compressor && this.audioContext) {
      // Use smooth transitions to avoid clicks (10ms for responsive slider feel)
      const smoothTime = 0.01;
      this.compressor.threshold.setTargetAtTime(threshold, this.audioContext.currentTime, smoothTime);
      this.compressor.ratio.setTargetAtTime(ratio, this.audioContext.currentTime, smoothTime);
      this.compressor.attack.setTargetAtTime(attack / 1000, this.audioContext.currentTime, smoothTime);
      this.compressor.release.setTargetAtTime(release / 1000, this.audioContext.currentTime, smoothTime);

      // Recalculate gain compensation since threshold/ratio affects auto-makeup
      const compressorSettings = store.getters['audioEffects/compressorSettings'];
      const autoMakeup = this.calculateAutoMakeupGain(threshold, ratio);
      const compensatedGain = compressorSettings.makeupGain - autoMakeup;
      this.gainNode.gain.setTargetAtTime(this.dbToGain(compensatedGain), this.audioContext.currentTime, smoothTime);
    }
  }

  /**
   * Calculate estimated automatic makeup gain that Web Audio applies
   * This is used to compensate/cancel out the unwanted auto-makeup
   *
   * Returns 0 if auto makeup gain is enabled (user wants the built-in behavior)
   *
   * @param {number} threshold - Threshold in dB (negative value)
   * @param {number} ratio - Compression ratio
   * @returns {number} - Estimated auto-makeup gain in dB (0 if auto makeup enabled)
   */
  calculateAutoMakeupGain(threshold, ratio) {
    const autoMakeupEnabled = store.getters['audioEffects/isAutoMakeupGainEnabled'];
    // Shared implementation with the offline renderer (fxChain.js)
    return fxCalculateAutoMakeupGain(threshold, ratio, autoMakeupEnabled);
  }

  /**
   * Update makeup gain (compensated for Web Audio's automatic makeup gain)
   * @param {number} gainDb - User's desired makeup gain in dB
   */
  updateMakeupGain(gainDb) {
    // Lazily initialize Web Audio when user changes effects
    this.ensureWebAudioInitialized();

    if (this.isWebAudioInitialized && this.gainNode && this.audioContext) {
      const compressorSettings = store.getters['audioEffects/compressorSettings'];
      const autoMakeup = this.calculateAutoMakeupGain(
        compressorSettings.threshold,
        compressorSettings.ratio
      );
      // Subtract auto-makeup to compensate, then add user's desired makeup
      const compensatedGain = gainDb - autoMakeup;
      // Use smooth transition to avoid clicks (10ms for responsive slider feel)
      this.gainNode.gain.setTargetAtTime(this.dbToGain(compensatedGain), this.audioContext.currentTime, 0.01);
    }
  }

  /**
   * Called when auto makeup gain setting changes
   * Re-applies the current makeup gain with or without compensation
   */
  updateAutoMakeupGain() {
    if (this.isWebAudioInitialized && this.gainNode && this.audioContext) {
      const compressorSettings = store.getters['audioEffects/compressorSettings'];
      const autoMakeup = this.calculateAutoMakeupGain(
        compressorSettings.threshold,
        compressorSettings.ratio
      );
      const compensatedGain = compressorSettings.makeupGain - autoMakeup;
      // Use smooth transition to avoid clicks (20ms time constant)
      this.gainNode.gain.setTargetAtTime(
        this.dbToGain(compensatedGain),
        this.audioContext.currentTime,
        0.02
      );
    }
  }

  /**
   * Get current compressor gain reduction in dB
   * Returns 0 when compressor is disabled to ensure meter shows no activity
   * @returns {number} - Gain reduction in dB (0 to -20 typically)
   */
  getCompressorReduction() {
    // Return 0 if compressor is disabled - meter should show no activity
    const compressorEnabled = store.getters['audioEffects/isCompressorEnabled'];
    if (!compressorEnabled) {
      return 0;
    }

    // Realtime graph (desktop): read the live compressor node
    if (this.isWebAudioInitialized && this.compressor) {
      return this.compressor.reduction;
    }

    // Offline path (iOS): estimated envelope sampled at the playback position
    if (this._reductionEnvelope && this.audio) {
      return envelopeValueAt(this._reductionEnvelope, this.audio.currentTime);
    }

    return 0;
  }

  /**
   * Whether the GR meter reads a live compressor node (desktop realtime
   * graph) as opposed to the estimated offline envelope used on iOS.
   * The dialog uses this to show the "changes take a moment" hint.
   * @returns {boolean}
   */
  isCompressorMeterLive() {
    return !isIOS();
  }

  /**
   * Enable or disable the compressor
   * When disabled, sets ratio to 1:1 (no compression)
   * @param {boolean} enabled - Whether compressor should be enabled
   */
  setCompressorEnabled(enabled) {
    if (!this.isWebAudioInitialized || !this.compressor || !this.audioContext) {
      return;
    }

    // Smooth transition time constant (20ms)
    const smoothTime = 0.02;

    if (enabled) {
      // Restore compressor settings from store
      const compressorSettings = store.getters['audioEffects/compressorSettings'];
      this.compressor.threshold.setTargetAtTime(compressorSettings.threshold, this.audioContext.currentTime, smoothTime);
      this.compressor.ratio.setTargetAtTime(compressorSettings.ratio, this.audioContext.currentTime, smoothTime);
      this.compressor.attack.setTargetAtTime(compressorSettings.attack / 1000, this.audioContext.currentTime, smoothTime);
      this.compressor.release.setTargetAtTime(compressorSettings.release / 1000, this.audioContext.currentTime, smoothTime);

      // Apply makeup gain with auto-makeup compensation
      const autoMakeup = this.calculateAutoMakeupGain(
        compressorSettings.threshold,
        compressorSettings.ratio
      );
      const compensatedGain = compressorSettings.makeupGain - autoMakeup;
      this.gainNode.gain.setTargetAtTime(this.dbToGain(compensatedGain), this.audioContext.currentTime, smoothTime);
    } else {
      // Bypass: set ratio to 1:1 (no compression) and unity gain
      this.compressor.ratio.setTargetAtTime(1, this.audioContext.currentTime, smoothTime);
      this.gainNode.gain.setTargetAtTime(1, this.audioContext.currentTime, smoothTime);
    }
  }

  /**
   * Enable or disable the EQ
   * When disabled, sets all band gains to 0 dB (flat response)
   * @param {boolean} enabled - Whether EQ should be enabled
   */
  setEqEnabled(enabled) {
    if (!this.isWebAudioInitialized || !this.eqFilters.length || !this.audioContext) {
      return;
    }

    // Smooth transition time constant (20ms)
    const smoothTime = 0.02;

    if (enabled) {
      // Restore EQ settings from store
      const eqSettings = store.getters['audioEffects/eqSettings'];
      const bands = ['highShelf', 'mid3', 'mid2', 'mid1', 'lowShelf'];
      bands.forEach((band, index) => {
        this.eqFilters[index].frequency.value = eqSettings[band].frequency;
        this.eqFilters[index].gain.setTargetAtTime(eqSettings[band].gain, this.audioContext.currentTime, smoothTime);
        this.eqFilters[index].Q.value = eqSettings[band].q;
      });
    } else {
      // Bypass: set all gains to 0 dB (flat response)
      this.eqFilters.forEach(filter => {
        filter.gain.setTargetAtTime(0, this.audioContext.currentTime, smoothTime);
      });
    }
  }

  /**
   * Convert dB to linear gain value
   * @param {number} db - Decibels
   * @returns {number} - Linear gain value
   */
  dbToGain(db) {
    return fxDbToGain(db);
  }

  /**
   * Reset Web Audio API (disconnect and clean up)
   */
  resetWebAudio() {
    if (this.isWebAudioInitialized) {
      try {
        if (this.sourceNode) {
          this.sourceNode.disconnect();
        }
        this.eqFilters.forEach(filter => filter.disconnect());
        if (this.compressor) {
          this.compressor.disconnect();
        }
        if (this.gainNode) {
          this.gainNode.disconnect();
        }
        if (this.seekGainNode) {
          this.seekGainNode.disconnect();
        }
      } catch (error) {
        console.error('Error disconnecting Web Audio nodes:', error);
      }

      this.sourceNode = null;
      this.sourceNodeAudioElement = null;
      this.eqFilters = [];
      this.compressor = null;
      this.gainNode = null;
      this.seekGainNode = null;
      this.isWebAudioInitialized = false;
    }
  }

  /**
   * Load a track by URL
   * @param {string} url - Track URL to load
   * @param {Function} callback - Optional callback after track is loaded
   * @returns {Promise} - Promise that resolves when track is loaded
   */
  /**
   * Prefetch a track URL as a blob so it can load instantly without network.
   * Call this while the current track is playing to warm the cache for the
   * next track. iOS Safari blocks network requests when the screen is locked,
   * so this ensures the next track is available in memory.
   * @param {string} url - The track URL to prefetch
   */
  prefetchTrack(url) {
    if (!url) return;

    // Already prefetched this URL
    if (this._prefetchedUrl === url && this._prefetchedBlobUrl) return;

    // Cancel any in-flight prefetch
    if (this._prefetchAbortController) {
      this._prefetchAbortController.abort();
    }

    // Revoke previous blob URL to free memory
    if (this._prefetchedBlobUrl) {
      URL.revokeObjectURL(this._prefetchedBlobUrl);
      this._prefetchedBlobUrl = null;
      this._prefetchedUrl = null;
    }

    this._prefetchAbortController = new AbortController();
    const abortController = this._prefetchAbortController;

    fetch(url, {
      mode: 'cors',
      signal: abortController.signal,
    })
      .then(response => {
        if (!response.ok) throw new Error('Prefetch failed');

        // Memory guard: never hold a very large file (e.g. a hi-fi WAV) as
        // an in-memory blob just to warm the next-track cache. Skipping the
        // prefetch means the next track streams from network instead —
        // far better than a 100+ MB resident allocation on an old iPhone.
        const contentLength = Number(
          response.headers && typeof response.headers.get === 'function'
            ? response.headers.get('content-length')
            : 0
        );
        if (contentLength && contentLength > AudioService.PREFETCH_MAX_BYTES) {
          abortController.abort();
          throw new Error('Prefetch skipped: file too large');
        }

        return response.blob();
      })
      .then(blob => {
        if (blob.size > AudioService.PREFETCH_MAX_BYTES) {
          // Defense in depth for servers that omit content-length
          this._prefetchAbortController = null;
          return;
        }
        this._prefetchedBlobUrl = URL.createObjectURL(blob);
        this._prefetchedUrl = url;
        this._prefetchAbortController = null;
      })
      .catch(() => {
        // Silently ignore — prefetch is best-effort
        this._prefetchAbortController = null;
      });
  }

  /**
   * Get the effective URL to load for a track.
   * If the URL was prefetched, returns the blob URL for instant loading.
   * Otherwise returns the original URL (requires network).
   * @param {string} url - The original track URL
   * @returns {string} - The URL to use (blob or original)
   */
  _getEffectiveUrl(url) {
    if (this._prefetchedUrl === url && this._prefetchedBlobUrl) {
      const blobUrl = this._prefetchedBlobUrl;
      // Clear the cache — this blob URL is now in use
      this._prefetchedBlobUrl = null;
      this._prefetchedUrl = null;
      return blobUrl;
    }

    // Hi-fi fallback: prefetch always stores the MP3 variant (WAV blobs are
    // too large to hold in memory). If the requested URL is the WAV of the
    // SAME track and iOS has the screen locked (network blocked), streaming
    // the WAV would fail and playback would stop at the track boundary —
    // continuing from the prefetched mp3 keeps the music going. The wav
    // streams again for later tracks once the network is available.
    if (
      this._prefetchedBlobUrl &&
      this._prefetchedUrl &&
      typeof url === 'string' &&
      url.endsWith('.wav') &&
      url.replace(/\.wav$/, '.mp3') === this._prefetchedUrl
    ) {
      const blobUrl = this._prefetchedBlobUrl;
      this._prefetchedBlobUrl = null;
      this._prefetchedUrl = null;
      return blobUrl;
    }

    return url;
  }

  /**
   * Immediately load and play a track with zero async delays.
   * Used for background track advancement where iOS will suspend the
   * web process if there is any gap in audio playback.
   *
   * Critical: this method is fully synchronous — no await, no rAF, no
   * Promises in the path to audio.play(). The play() Promise is fire-and-forget.
   *
   * @param {string} url - Track URL to load
   * @returns {boolean} - True if play() was called
   */
  loadAndPlayImmediate(url) {
    if (!this.audio || !url) return false;

    // Hybrid routing bookkeeping (synchronous — must not delay play())
    this._hasUserInitiatedPlayback = true;
    this._ensureFxSubscription();
    this._onTrackSourceChanging(url);

    // Use prefetched blob URL if available (loads from memory, no network)
    const effectiveUrl = this._getEffectiveUrl(url);

    // Reset end-detection and seek state for the new track
    this._endDetectionFired = false;
    if (this._nearEndTimeout) {
      clearTimeout(this._nearEndTimeout);
      this._nearEndTimeout = null;
    }
    this._cleanupSeekState();

    // Resume AudioContext if needed (fire-and-forget)
    if (this.audioContext && this.audioContext.state !== 'running') {
      this.audioContext.resume().catch(() => {});
    }

    // Set volume to full (may be at 0 from previous track's fade-out)
    this.audio.volume = 1;

    // Set src and play — both calls are synchronous.
    // play() returns a Promise but we don't await it; iOS sees the
    // play request and keeps the process alive while audio loads.
    this.audio.src = effectiveUrl;
    this.isPlaying = true;
    this.isLoading = false;
    this._syncPlaybackState();

    // A prefetched blob is now the element's src: queue it for revocation
    // at the NEXT source change instead of revoking mid-use — iOS media
    // loads lazily, and killing the src's blob can break later fetches.
    if (effectiveUrl !== url && effectiveUrl.indexOf('blob:') === 0) {
      this._queuePendingRevoke(effectiveUrl);
    }

    const playPromise = this.audio.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(() => {
          // Release blobs that are no longer the current src
          this._flushPendingRevoke();
        })
        .catch(() => {
          this._markPlaybackFailed();
        });
    }
    // iOS can leave the play() promise pending forever; confirm playback
    // via the element's 'playing' event and self-heal the flags if it
    // never starts (a stale isPlaying=true used to dead-end the
    // lock-screen play button).
    this._armPlaybackConfirmation();

    // Keep AudioContext alive (no-op on iOS / native mode)
    this.startAudioContextKeepAlive();

    // iOS + FX on: render the FX'd version in the background and hot-swap.
    // Scheduled after play() so the synchronous play path stays untouched.
    if (isIOS() && store.getters['audioEffects/hasActiveEffects']) {
      this._scheduleOfflineRender(0);
    }

    this._syncPlaybackState();

    return true;
  }

  async loadTrack(url, callback = null) {
    if (!url) {
      return Promise.resolve();
    }

    this.isLoading = true;

    // Hybrid routing bookkeeping: a new source invalidates offline renders
    this._ensureFxSubscription();
    this._onTrackSourceChanging(url);

    // Reuse the same Audio element to preserve iOS Safari's user-gesture
    // activation for background playback. Creating new Audio() would lose
    // the privilege and prevent .play() when the screen is locked.
    const audio = this._ensureAudioElement();

    // Internal reload: the pause below and the src swap must not read as an
    // OS interruption, and the transport intent is left alone — a track
    // change keeps it on (spinner until the new track plays), the page-load
    // preload leaves it off. Closed again when the load settles.
    this._transitionDepth++;
    this._syncPlaybackState();
    const settleTransition = () => {
      this._transitionDepth = Math.max(0, this._transitionDepth - 1);
      this._syncPlaybackState();
    };

    try {
      // If audio is playing, fade out first to avoid click
      if (this.isPlaying || !audio.paused) {
        await this._fadeOutElement(80);
      } else {
        audio.volume = 0;
      }

      // NOTE: We do NOT disconnect the sourceNode here. Since the audio element
      // is the same, the MediaElementSourceNode remains valid when src changes.

      // Clear any pending seek state from previous track
      this._cleanupSeekState();

      // Reset end-detection state for the new track
      this._endDetectionFired = false;
      if (this._nearEndTimeout) {
        clearTimeout(this._nearEndTimeout);
        this._nearEndTimeout = null;
      }
    } catch (error) {
      // Never leave the transition window open — it would mask every later
      // external pause
      settleTransition();
      throw error;
    }

    return new Promise((resolve) => {
      let resolved = false;
      const resolveOnce = () => {
        if (!resolved) {
          resolved = true;
          settleTransition();
          resolve();
        }
      };

      audio.onloadeddata = () => {
        // Self-detach: this handler belongs to THIS loadTrack call only.
        // Leaving it attached would make it re-fire on every later src
        // change (e.g., FX hot-swaps), running stale callbacks.
        audio.onloadeddata = null;
        audio.onerror = null;

        this.isLoading = false;
        // A prefetched blob src is queued for revocation at the NEXT source
        // change (revoking a blob that is still the current src can break
        // iOS's lazy media fetches, e.g. on seek). Old queued blobs that
        // are no longer the src get released now.
        if (effectiveUrl !== url && effectiveUrl.indexOf('blob:') === 0) {
          this._queuePendingRevoke(effectiveUrl);
        }
        this._flushPendingRevoke();
        if (callback) {
          callback();
        }
        resolveOnce();
      };

      audio.onerror = () => {
        audio.onloadeddata = null;
        audio.onerror = null;
        this.isLoading = false;
        resolveOnce();
      };

      // Timeout in case onloadeddata never fires
      setTimeout(() => {
        this.isLoading = false;
        resolveOnce();
      }, 3000);

      // Use prefetched blob URL if available (instant load, no network needed)
      // This is critical for iOS background playback where network is blocked
      const effectiveUrl = this._getEffectiveUrl(url);
      audio.src = effectiveUrl;
      audio.load();

      // iOS + FX on: start natively right away (no start-up delay), render
      // the FX'd version in the background and hot-swap when it is ready.
      // Gated on playback having been user-initiated at least once — the
      // initial page-load loadTrack must NOT kick off a render just because
      // FX settings persisted from a previous session.
      if (
        isIOS() &&
        this._hasUserInitiatedPlayback &&
        store.getters['audioEffects/hasActiveEffects']
      ) {
        this._scheduleOfflineRender(0);
      }
    });
  }

  /**
   * Set a callback function to be called when track ends
   * @param {Function} callback - Function to call when track ends
   */
  setTrackEndedCallback(callback) {
    // Remove old wrapped callback from the audio element
    if (this._wrappedEndedCallback && this.audio) {
      this.audio.removeEventListener("ended", this._wrappedEndedCallback);
      this._wrappedEndedCallback = null;
    }

    this.trackEndedCallback = callback;

    // Attach to the current audio element if it exists
    if (this.audio && callback) {
      this._attachEndedListener(callback);
    }
  }

  /**
   * Play the current track
   * @returns {Promise<boolean>} - True if playback started successfully, false otherwise
   */
  async play() {
    if (!this.audio) {
      return false;
    }

    try {
      this._hasUserInitiatedPlayback = true;
      this._ensureFxSubscription();

      // Transport on from this moment: the button shows the spinner (then
      // "pause") while the element gets going. The 'playing' confirmation
      // below settles it, and a rejection or a play() that never settles
      // turns it back off — no optimistic flag can get stranded.
      this.isPlaying = true;
      this._armPlaybackConfirmation();
      this._syncPlaybackState();

      if (!isIOS()) {
        // Fallback: Create AudioContext if it doesn't exist (should already be created by interaction)
        if (!this.audioContext) {
          this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        }

        // Resume AudioContext if suspended
        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
        }

        // Hybrid gating: keep the graph wired if this element was already
        // captured (createMediaElementSource cannot be undone), otherwise
        // only initialize it when FX are actually enabled. With FX off the
        // element plays through the native pipeline.
        if (this.isWebAudioInitialized) {
          if (this.audio !== this.sourceNodeAudioElement) {
            // Audio element changed (e.g., new track loaded after effects were adjusted)
            this.reconnectWebAudio();
          }
        } else if (store.getters['audioEffects/hasActiveEffects']) {
          this.initializeWebAudio();
        }
      } else if (
        store.getters['audioEffects/hasActiveEffects'] &&
        !this._activeSwapBlobUrl &&
        !this._offlineRenderTimer
      ) {
        // iOS with FX on but no rendered source yet (e.g., FX were enabled
        // before any track was loaded): kick off an offline render now.
        this._scheduleOfflineRender(0);
      }

      // Fade in on play to avoid startup clicks
      // Always target full volume (1.0) — audio.volume may be 0 from
      // loadTrack() which zeros it to prevent clicks during src change.
      this.audio.volume = 0;

      await this.audio.play();
      // (isPlaying is not re-asserted here: a pause that landed while the
      // promise was pending — user or OS — must win.)

      // Smooth fade-in over 50ms
      this.fadeInVolume(1, 50);

      // Verify AudioContext is running after playback starts
      if (this.audioContext && this.audioContext.state !== 'running') {
        await this.audioContext.resume();
      }

      // Start keep-alive to prevent iOS from suspending AudioContext
      this.startAudioContextKeepAlive();

      this._syncPlaybackState();
      return true;
    } catch (error) {
      console.error('Error playing audio:', error);
      // Turns the transport off and publishes — unless a reload of ours
      // interrupted this play() (AbortError), in which case the reload's own
      // play() follows.
      this._markPlaybackFailed();
      return false;
    }
  }

  /**
   * Pause the current track
   */
  pause() {
    this.isPlaying = false;
    this._disarmPlaybackConfirmation();
    this.stopAudioContextKeepAlive();
    if (this.audio) {
      this.audio.pause();
    }
    this._syncPlaybackState();
  }

  /**
   * Fade out audio and then pause — the user-facing pause (button, Media
   * Session, switching to video mode): the transport goes off immediately
   * so the UI reacts on the tap, then the element fades out.
   * @param {number} duration - Fade duration in milliseconds (default 200ms)
   * @returns {Promise} - Resolves when fade and pause are complete
   */
  fadeOutAndPause(duration = 200) {
    this.isPlaying = false;
    this._disarmPlaybackConfirmation();
    this.stopAudioContextKeepAlive();
    this._syncPlaybackState();
    return this._fadeOutElement(duration);
  }

  /**
   * Fade the element out and pause it WITHOUT touching the transport intent
   * (internal reloads: a track change keeps the intent on so the button
   * shows the spinner until the next track plays).
   * Uses HTML5 audio.volume for reliable fading (works without Web Audio setup)
   * @param {number} duration - Fade duration in milliseconds
   * @returns {Promise} - Resolves when fade and pause are complete
   */
  _fadeOutElement(duration = 200) {
    return new Promise((resolve) => {
      if (!this.audio) {
        resolve();
        return;
      }

      // Fast path — pause immediately, no rAF fade:
      // - iOS: programmatic audio.volume is ignored on iPhone, so the fade
      //   only adds latency without fading anything.
      // - Hidden page (screen locked / CarPlay with display off): rAF does
      //   not fire, so an rAF-driven fade would STALL the promise until the
      //   screen wakes — track changes issued from the lock screen or a car
      //   head unit would hang on it.
      const pageHidden = typeof document !== 'undefined' && document.hidden;
      if (isIOS() || pageHidden || duration <= 0) {
        this.audio.volume = 0;
        this.audio.pause();
        resolve();
        return;
      }

      const startTime = performance.now();
      const startVolume = this.audio.volume;

      // Use requestAnimationFrame for smooth volume fade
      const fadeStep = () => {
        // Check if audio element still exists (might have been replaced during fade)
        if (!this.audio) {
          resolve();
          return;
        }

        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Linear fade
        this.audio.volume = startVolume * (1 - progress);

        if (progress < 1) {
          requestAnimationFrame(fadeStep);
        } else {
          // Fade complete - pause (keep volume at 0 to prevent any blip)
          this.audio.pause();
          resolve();
        }
      };

      requestAnimationFrame(fadeStep);
    });
  }

  /**
   * Fade in volume from current level to target
   * Used when starting playback to avoid startup clicks
   * @param {number} targetVolume - Target volume (0-1)
   * @param {number} duration - Fade duration in milliseconds
   */
  fadeInVolume(targetVolume, duration = 50) {
    if (!this.audio) return;

    const startTime = performance.now();
    const startVolume = this.audio.volume;
    const volumeDiff = targetVolume - startVolume;

    const fadeStep = () => {
      if (!this.audio) return;

      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      this.audio.volume = startVolume + (volumeDiff * progress);

      if (progress < 1) {
        requestAnimationFrame(fadeStep);
      }
    };

    requestAnimationFrame(fadeStep);
  }

  /**
   * Seek to a specific position in the track
   * @param {number} positionPercent - Position as percentage (0-100)
   */
  seek(positionPercent) {
    if (this.audio) {
      const timeToJumpTo = (positionPercent / 100) * this.audio.duration;
      this.audio.currentTime = timeToJumpTo;
    }
  }

  /**
   * Seek with fade out/in to avoid clicks
   * Includes debouncing for rapid seeks and extended pending position for iOS Safari
   * @param {number} positionPercent - Position as percentage (0-100)
   * @param {number} fadeDuration - Duration for each fade (ms)
   */
  seekWithFade(positionPercent, fadeDuration = 50) {
    if (!this.audio || !this.audio.duration) return;

    const now = performance.now();
    const timeToJumpTo = (positionPercent / 100) * this.audio.duration;

    // iOS Safari workaround (WebKit Bug #204228):
    // When audio is connected to AudioContext, currentTime becomes unreliable during seek.
    // Store the target position so getTrackProgress() can return stable values.
    // Set this IMMEDIATELY before any async operations to prevent UI flicker.
    this.pendingSeekPosition = timeToJumpTo;

    // Clear any existing pending seek timeout
    if (this.pendingSeekTimeout) {
      clearTimeout(this.pendingSeekTimeout);
      this.pendingSeekTimeout = null;
    }

    // Debounce rapid seeks - if seeking too fast, just update the pending position
    // but don't actually seek yet (reduces audio glitches on rapid tapping)
    const timeSinceLastSeek = now - this.lastSeekTime;
    if (timeSinceLastSeek < this.seekDebounceMs) {
      // Schedule this seek to happen after debounce period
      this.pendingSeekTimeout = setTimeout(() => {
        this.executeSeek(timeToJumpTo, fadeDuration);
      }, this.seekDebounceMs - timeSinceLastSeek);
      return;
    }

    this.executeSeek(timeToJumpTo, fadeDuration);
  }

  /**
   * Clean up all seek-related state (timers, animations, gain automation).
   * Called when loading a new track or resetting the service.
   */
  _cleanupSeekState() {
    this.pendingSeekPosition = null;
    if (this.pendingSeekTimeout) {
      clearTimeout(this.pendingSeekTimeout);
      this.pendingSeekTimeout = null;
    }
    if (this.seekAnimationFrame) {
      cancelAnimationFrame(this.seekAnimationFrame);
      this.seekAnimationFrame = null;
    }
    this._cancelPendingSeekFadeIn();
    if (this.seekGainNode && this.audioContext) {
      const now = this.audioContext.currentTime;
      this.seekGainNode.gain.cancelScheduledValues(now);
      this.seekGainNode.gain.setValueAtTime(1, now);
    }
    this.lastSeekTime = 0;
  }

  /**
   * Execute the actual seek operation.
   * Uses Web Audio GainNode for sample-accurate fading when available,
   * falls back to rAF-based audio.volume fading otherwise.
   * @param {number} timeToJumpTo - Time in seconds to seek to
   * @param {number} fadeDuration - Duration for fade-in phase (ms)
   */
  executeSeek(timeToJumpTo, fadeDuration) {
    if (!this.audio) return;

    // Cancel any in-progress fallback animation
    if (this.seekAnimationFrame) {
      cancelAnimationFrame(this.seekAnimationFrame);
      this.seekAnimationFrame = null;
    }

    this.lastSeekTime = performance.now();
    this.pendingSeekPosition = timeToJumpTo;

    if (this.pendingSeekTimeout) {
      clearTimeout(this.pendingSeekTimeout);
      this.pendingSeekTimeout = null;
    }

    // Clear pending position after the seek settles
    const onSeeked = () => {
      this.audio.removeEventListener('seeked', onSeeked);
      this.pendingSeekTimeout = setTimeout(() => {
        if (this.pendingSeekPosition === timeToJumpTo) {
          this.pendingSeekPosition = null;
        }
        this.pendingSeekTimeout = null;
      }, 100);
    };
    this.audio.addEventListener('seeked', onSeeked);

    if (this.isWebAudioInitialized && this.seekGainNode && this.audioContext) {
      this._executeSeekWebAudio(timeToJumpTo, fadeDuration);
    } else {
      this._executeSeekFallback(timeToJumpTo, fadeDuration);
    }
  }

  /**
   * Cancel any pending fade-in from a previous seek (seeked handler + timeouts).
   */
  _cancelPendingSeekFadeIn() {
    if (this._seekFadeHandler && this.audio) {
      this.audio.removeEventListener('seeked', this._seekFadeHandler);
    }
    this._seekFadeHandler = null;
    if (this._seekFadeTimeout) {
      clearTimeout(this._seekFadeTimeout);
      this._seekFadeTimeout = null;
    }
  }

  /**
   * Sample-accurate seek using Web Audio GainNode.
   *
   * 1. Micro-fade-out old audio (1ms time constant → hard zero at 3ms)
   * 2. Seek immediately (async — browser decodes data at new position)
   * 3. Wait for seeked event, then fade in via Web Audio automation
   *
   * The fade-out uses a 1ms time constant (95% silent by 3ms) followed by a
   * hard lock to zero. This is critical for iOS Safari, where WebKit's
   * AVFoundation-backed pipeline can deliver decoded audio from the new seek
   * position in as little as 3-5ms — much faster than Chrome's ~10-20ms.
   *
   * The fade-in is scheduled after the browser's seeked event instead of a
   * fixed delay, so it adapts to the actual decoder latency on each platform.
   */
  _executeSeekWebAudio(timeToJumpTo, fadeDuration) {
    const ctx = this.audioContext;
    const now = ctx.currentTime; // Capture once (can differ between reads in Chrome)
    const gain = this.seekGainNode.gain;
    const fadeSeconds = fadeDuration / 1000;

    // Cancel any in-progress gain automation and pending fade-in from a previous seek
    gain.cancelScheduledValues(now);
    this._cancelPendingSeekFadeIn();

    // Fast micro-fade-out of currently-playing audio (1ms time constant).
    // setTargetAtTime decays from whatever the current gain level is, so it
    // handles interruption of a previous seek's fade-in gracefully.
    // Hard lock to exact zero at 3ms guarantees silence before iOS Safari's
    // fast decoder delivers audio from the new position.
    gain.setTargetAtTime(0, now, 0.001);
    gain.setValueAtTime(0, now + 0.003);

    // Seek immediately — responsive playhead (audio.currentTime updates synchronously)
    this.audio.currentTime = timeToJumpTo;

    // Schedule fade-in after the browser signals the seek is complete.
    // This adapts to actual decoder latency on each platform instead of a
    // fixed delay that's too short on iOS Safari or too long on Chrome.
    const scheduleFadeIn = () => {
      this._cancelPendingSeekFadeIn();
      const fadeNow = ctx.currentTime;
      gain.cancelScheduledValues(fadeNow);
      gain.setValueAtTime(0, fadeNow);
      gain.linearRampToValueAtTime(1, fadeNow + fadeSeconds);
    };

    this._seekFadeHandler = () => {
      this._seekFadeHandler = null;
      // Clear fallback timeout since seeked fired normally
      if (this._seekFadeTimeout) {
        clearTimeout(this._seekFadeTimeout);
      }
      // Short post-seeked settle for decoder stabilization, then fade in
      this._seekFadeTimeout = setTimeout(scheduleFadeIn, 10);
    };
    this.audio.addEventListener('seeked', this._seekFadeHandler, { once: true });

    // Safety fallback: if seeked doesn't fire within 100ms, fade in anyway
    this._seekFadeTimeout = setTimeout(scheduleFadeIn, 100);
  }

  /**
   * Fallback seek using audio.volume with requestAnimationFrame.
   * Used before first play() when Web Audio isn't initialized yet.
   */
  _executeSeekFallback(timeToJumpTo, fadeDuration) {
    const targetVolume = this.audio.volume || 1;

    // Snap volume to 0, seek immediately, then fade back in
    this.audio.volume = 0;
    this.audio.currentTime = timeToJumpTo;

    const startTime = performance.now();
    const fadeStep = () => {
      if (!this.audio) return;
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / fadeDuration, 1);
      this.audio.volume = targetVolume * progress;
      if (progress < 1) {
        this.seekAnimationFrame = requestAnimationFrame(fadeStep);
      } else {
        this.seekAnimationFrame = null;
      }
    };

    // Short delay for decoder to stabilize, then fade in
    setTimeout(() => {
      this.seekAnimationFrame = requestAnimationFrame(fadeStep);
    }, 20);
  }

  /**
   * Get current track progress information
   * @returns {Object} - Object containing current position, duration and progress percentage
   */
  getTrackProgress() {
    if (!this.audio) {
      return {
        currentPosition: "00:00",
        duration: "00:00",
        progressPercent: 0,
        currentTime: 0,
        durationTime: 0
      };
    }

    // iOS Safari workaround (WebKit Bug #204228):
    // When audio is connected to AudioContext and seeking, currentTime is unreliable.
    // Use the pending seek position if we're in the middle of a seek operation.
    const effectiveCurrentTime = (this.pendingSeekPosition !== null || this.audio.seeking)
      ? (this.pendingSeekPosition ?? this.audio.currentTime)
      : this.audio.currentTime;

    const currentPosition = this.formatTime(effectiveCurrentTime);
    const duration = this.formatTime(this.audio.duration);
    const progressPercent = (effectiveCurrentTime / this.audio.duration) * 100 || 0;

    return {
      currentPosition,
      duration,
      progressPercent,
      currentTime: effectiveCurrentTime,
      durationTime: this.audio.duration
    };
  }

  /**
   * Monitor track for playback issues
   * @param {Function} retryCallback - Function to call when retry is needed
   * @param {Function} nextTrackCallback - Function to call when loading next track
   * @returns {boolean} - True if track needed intervention, false otherwise
   */
  monitorTrackPlayback(retryCallback, nextTrackCallback) {
    // A hot-swap briefly pauses/reloads the element; don't mistake it for a stall
    if (this._swapInProgress) {
      return false;
    }

    // Periodic truth check: an element found paused with the transport
    // still "on" (an interruption whose event never reached us) turns the
    // transport off instead of being treated as a stall to retry.
    this._reconcileWithElement();

    if (
      this.isPlaying && 
      this.previousTrackPosition === this.getTrackProgress().currentPosition
    ) {
      if (this.sameTrackPositionCounter > 2) {
        if (this.trackReloadCounter > 2) {
          // Give up and move to next track
          this.trackReloadCounter = 0;
          nextTrackCallback();
          return true;
        } else {
          // Try to reload the current track
          this.trackReloadCounter += 1;
          retryCallback();
          return true;
        }
      } else {
        this.sameTrackPositionCounter += 1;
        return false;
      }
    } else {
      // Track is playing normally, reset counters
      this.sameTrackPositionCounter = 0;
      this.trackReloadCounter = 0;
      this.previousTrackPosition = this.getTrackProgress().currentPosition;
      return false;
    }
  }

  /**
   * Format seconds into MM:SS format
   * @param {number} numberOfSeconds - Number of seconds to format
   * @returns {string} - Formatted time string
   */
  formatTime(numberOfSeconds) {
    if (!numberOfSeconds || isNaN(numberOfSeconds)) {
      return "00:00";
    }

    const date = new Date(0);
    date.setSeconds(numberOfSeconds);
    return date.toISOString().substring(14, 19);
  }

  // ---------------------------------------------------------------------
  // Hybrid audio routing (iOS offline FX rendering)
  // See HYBRID_AUDIO_ROUTING_PLAN.md
  // ---------------------------------------------------------------------

  /**
   * Deep-cloned snapshot of the FX settings from the store, in the shape
   * buildFxChain expects. Cloned so an in-flight offline render is not
   * affected by concurrent slider changes.
   * @returns {object}
   */
  _getFxSettingsSnapshot() {
    return {
      eqSettings: JSON.parse(JSON.stringify(store.getters['audioEffects/eqSettings'])),
      compressorSettings: JSON.parse(JSON.stringify(store.getters['audioEffects/compressorSettings'])),
      eqEnabled: store.getters['audioEffects/isEqEnabled'],
      compressorEnabled: store.getters['audioEffects/isCompressorEnabled'],
      autoMakeupEnabled: store.getters['audioEffects/isAutoMakeupGainEnabled'],
      // The GR envelope exists only for the FX dialog's meter — skip the
      // full-track scan when the dialog isn't open. (If the dialog is opened
      // later, the meter comes alive on the next settings change.)
      wantEnvelope: !!store.getters['audioEffects/isAudioEffectsDialogOpen']
    };
  }

  /**
   * iOS only: subscribe to audioEffects store mutations so ANY settings
   * change (sliders, toggles, reset-to-default) triggers an offline
   * re-render. The dialog dispatches a store mutation for every change, so
   * this catches paths the dialog guards behind isWebAudioInitialized.
   * Registered lazily (not in the constructor) to avoid touching the store
   * during module initialization (import cycle with the store modules).
   */
  _ensureFxSubscription() {
    if (this._fxSubscriptionRegistered || !isIOS()) {
      return;
    }
    this._fxSubscriptionRegistered = true;

    store.subscribe((mutation) => {
      if (!mutation.type || mutation.type.indexOf('audioEffects/') !== 0) {
        return;
      }
      if (mutation.type === 'audioEffects/SET_DIALOG_OPEN') {
        return;
      }
      this._handleIOSFxSettingsChange();
    });
  }

  /**
   * iOS: react to an FX settings change.
   * - FX active: debounce, then render offline and hot-swap.
   * - All FX off: cancel renders and swap back to the clean source.
   */
  _handleIOSFxSettingsChange() {
    if (!isIOS() || !this.audio || !this._currentTrackUrl) {
      return;
    }

    if (store.getters['audioEffects/hasActiveEffects']) {
      this._scheduleOfflineRender(400);
    } else {
      this._cancelPendingOfflineRender();
      OfflineFxService.bumpGeneration(); // discard any in-flight render
      this._revertToOriginalSource();
    }
  }

  /**
   * Debounced kick-off of an offline render for the current track.
   * @param {number} delayMs
   */
  _scheduleOfflineRender(delayMs) {
    this._cancelPendingOfflineRender();
    this._offlineRenderTimer = setTimeout(() => {
      this._offlineRenderTimer = null;
      this._runOfflineRender();
    }, delayMs);
  }

  _cancelPendingOfflineRender() {
    if (this._offlineRenderTimer) {
      clearTimeout(this._offlineRenderTimer);
      this._offlineRenderTimer = null;
    }
  }

  /**
   * Render the current track through the FX chain offline and hot-swap the
   * result in. Any failure leaves native (unprocessed) playback running —
   * degraded to "no FX", never to "no audio".
   */
  async _runOfflineRender() {
    const url = this._currentTrackUrl;
    if (!url || !this.audio) {
      return;
    }

    // Memory guard: an offline render of a very long track allocates two
    // full-length Float32 buffers plus the WAV (roughly 1 GB total for a
    // 20-minute track) — guaranteed tab death on an older iPhone. Play such
    // tracks clean instead.
    const trackDuration = this.audio.duration;
    if (isFinite(trackDuration) && trackDuration > AudioService.OFFLINE_FX_MAX_SECONDS) {
      console.info(
        '[FX] track too long for offline FX rendering (' +
          Math.round(trackDuration) +
          's) — playing without FX'
      );
      if (this._fxTooLongNotifiedUrl !== url) {
        this._fxTooLongNotifiedUrl = url;
        try {
          store.dispatch('ui/showSnackbar', {
            text: "FX can't be applied to tracks this long on iPhone/iPad — playing without FX",
            timeout: 4000,
          });
        } catch (e) {
          // snackbar is cosmetic
        }
      }
      return;
    }

    try {
      const settings = this._getFxSettingsSnapshot();
      const result = await OfflineFxService.render(url, settings);
      if (!result) {
        console.info('[FX] offline render returned no result (superseded or failed)');
        return; // superseded by newer render/track change, or failed
      }
      if (url !== this._currentTrackUrl) {
        URL.revokeObjectURL(result.blobUrl); // track changed while rendering
        return;
      }
      await this._swapToSource(result.blobUrl, result.reductionEnvelope || null);
    } catch (error) {
      console.error('Offline FX render failed; continuing without FX:', error);
    }
  }

  /**
   * Hot-swap the audio element to a new source, preserving position and
   * play state. Used on iOS to switch between the clean file and the
   * FX-rendered WAV blob.
   *
   * Deliberately NO rAF-driven fades here: right after a render the main
   * thread can be busy for seconds (WAV encode + GC on older devices), and
   * a stalled fade keeps audio playing past the captured position — resuming
   * there replays content ("skips back"). Instead: synchronous pause, read
   * currentTime AT the pause, and compensate for the real swap duration so
   * wall-clock continuity is preserved.
   *
   * @param {string} newSrc - blob URL or original track URL
   * @param {{rate:number, values:Float32Array}|null} reductionEnvelope
   *        Estimated GR envelope for the meter (null to clear)
   */
  async _swapToSource(newSrc, reductionEnvelope = null) {
    const audio = this.audio;
    const isBlob = typeof newSrc === 'string' && newSrc.indexOf('blob:') === 0;

    if (!audio) {
      if (isBlob) URL.revokeObjectURL(newSrc);
      return;
    }

    const now = () =>
      typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();

    if (this._swapInProgress) {
      // Self-heal: a swap stuck past the threshold means some iOS promise
      // never settled — without this, every later FX change would queue
      // behind a dead swap forever.
      const stuckMs = this._swapStartedAt ? now() - this._swapStartedAt : 0;
      if (stuckMs > this._staleSwapThresholdMs) {
        console.warn('[FX] previous swap stuck for ' + Math.round(stuckMs) + 'ms — force-clearing');
        this._swapInProgress = false;
      } else {
        // Queue the LATEST request; it runs when the current swap finishes.
        // (Dropping it would lose e.g. an FX-off revert issued mid-swap.)
        if (this._queuedSwap && this._queuedSwap.isBlob) {
          URL.revokeObjectURL(this._queuedSwap.newSrc);
        }
        this._queuedSwap = { newSrc, reductionEnvelope, isBlob };
        return;
      }
    }

    this._swapStartedAt = now();
    this._swapInProgress = true;
    try {
      const wasPlaying = this.isPlaying && !audio.paused;

      // Preferred path: gapless dual-element switch. The rendered version
      // starts muted in the standby element, syncs to the live clock, then
      // the elements flip roles — playback never stops.
      // Skipped when hidden: media events are unreliable in background tabs
      // and the instant swap is more robust there.
      const hidden = typeof document !== 'undefined' && document.hidden;
      if (wasPlaying && !hidden && isIOS() && this._standbyAudio) {
        const result = await this._seamlessSwapToSource(newSrc, reductionEnvelope, isBlob);
        if (result === 'done') {
          return;
        }
        if (result === 'superseded') {
          if (isBlob) URL.revokeObjectURL(newSrc);
          return;
        }
        // result === 'fallback': fall through to the instant swap below
      }

      const previousSwapUrl = this._activeSwapBlobUrl;

      // Instant, jank-immune stop; position read at the actual stop moment
      audio.pause();
      const position = audio.currentTime || 0;
      const swapStart = now();

      // Load the new source and wait for it (3s fallback, as in loadTrack)
      await new Promise((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          audio.removeEventListener('loadeddata', finish);
          audio.removeEventListener('error', finish);
          resolve();
        };
        audio.addEventListener('loadeddata', finish);
        audio.addEventListener('error', finish);
        audio.src = newSrc;
        audio.load();
        setTimeout(finish, 3000);
      });

      // Resume where playback actually stopped, plus the (capped) real time
      // the swap took — skip the tiny gap forward rather than replaying it.
      const gapSec = Math.min(3, (now() - swapStart) / 1000);
      let target = position + (wasPlaying ? gapSec : 0);
      if (isFinite(audio.duration) && audio.duration > 0) {
        target = Math.min(target, Math.max(0, audio.duration - 0.05));
      }
      try {
        audio.currentTime = target;
      } catch (e) {
        // Not seekable yet — extremely unlikely for blob/WAV
      }

      if (wasPlaying) {
        try {
          // Bound play() too — iOS can leave the promise pending
          const playPromise = audio.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {}); // silence late rejections after timeout
          }
          await Promise.race([
            playPromise,
            new Promise((resolve) => setTimeout(resolve, 4000))
          ]);
          this.isPlaying = true;
          audio.volume = 1;

          // iOS occasionally drops a seek issued right at loadeddata and
          // starts from 0 — verify the seek stuck and reissue once.
          if (target > 2 && audio.currentTime < target - 2) {
            try {
              audio.currentTime = target;
            } catch (e) {
              // keep playing from wherever we are rather than interrupting
            }
          }

        } catch (error) {
          console.error('Error resuming playback after FX swap:', error);
          this.isPlaying = false;
        }
      } else {
        audio.volume = 1;
      }

      // Meter envelope follows the source that is now playing
      this._reductionEnvelope = reductionEnvelope;

      // Blob URL lifecycle: track the new one, release the previous one
      this._activeSwapBlobUrl = isBlob ? newSrc : null;
      if (previousSwapUrl && previousSwapUrl !== newSrc) {
        URL.revokeObjectURL(previousSwapUrl);
      }
    } finally {
      this._swapInProgress = false;
      // Element pauses were masked during the swap — publish the real state
      this._syncPlaybackState();

      // Run the most recent swap requested while this one was in flight
      if (this._queuedSwap) {
        const queued = this._queuedSwap;
        this._queuedSwap = null;
        this._swapToSource(queued.newSrc, queued.reductionEnvelope);
      }
    }
  }

  /**
   * Gapless switch to a new source using the standby element.
   *
   * iPhone Safari ignores programmatic volume, so a true crossfade is not
   * possible — instead: load the new source into the muted standby element,
   * start it, align its clock to the live element (both carry the same
   * track, just processed vs. clean), then unmute the standby and pause the
   * old element ~40 ms later. The brief overlap plays identical content at
   * the same position, so the ear hears continuous music with the FX
   * snapping in.
   *
   * @param {string} newSrc
   * @param {{rate:number, values:Float32Array}|null} reductionEnvelope
   * @param {boolean} isBlob
   * @returns {Promise<'done'|'fallback'|'superseded'>}
   */
  async _seamlessSwapToSource(newSrc, reductionEnvelope, isBlob) {
    const active = this.audio;
    const standby = this._standbyAudio;
    if (!active || !standby) {
      return 'fallback';
    }

    const gen = this._swapGen;
    const SYNC_LEAD_SEC = 0.15; // rough head start while we wait for playback
    const SWITCH_LEAD_SEC = 0.05; // covers seek/unmute latency at the flip
    const OVERLAP_MS = 40;

    try {
      standby.muted = true;

      // Load the rendered source into the standby element
      const loaded = await new Promise((resolve) => {
        let done = false;
        const finish = (ok) => {
          if (done) return;
          done = true;
          standby.removeEventListener('loadeddata', onLoad);
          standby.removeEventListener('error', onError);
          resolve(ok);
        };
        const onLoad = () => finish(true);
        const onError = () => finish(false);
        standby.addEventListener('loadeddata', onLoad);
        standby.addEventListener('error', onError);
        standby.src = newSrc;
        standby.load();
        setTimeout(() => finish(false), 4000);
      });
      if (gen !== this._swapGen) return 'superseded';
      if (!loaded) return 'fallback';

      // Start muted, slightly ahead of the live position
      try {
        standby.currentTime = Math.max(0, (active.currentTime || 0) + SYNC_LEAD_SEC);
      } catch (e) {
        console.info('[FX] seamless fallback: standby seek failed');
        return 'fallback';
      }

      // iOS can leave a play() promise pending forever — bound it.
      const playOutcome = await Promise.race([
        standby.play().then(
          () => 'ok',
          (e) => {
            console.info('[FX] seamless fallback: standby play refused:', e && e.name);
            return 'refused';
          }
        ),
        new Promise((resolve) =>
          setTimeout(() => resolve('timeout'), this._seamlessPlayTimeoutMs)
        )
      ]);
      if (playOutcome !== 'ok') {
        if (playOutcome === 'timeout') {
          console.info('[FX] seamless fallback: standby play() never settled');
        }
        try {
          standby.pause();
        } catch (e) {
          // ignore
        }
        return gen !== this._swapGen ? 'superseded' : 'fallback';
      }
      if (gen !== this._swapGen) {
        standby.pause();
        return 'superseded';
      }

      // Wait until the standby clock is actually advancing
      const started = await new Promise((resolve) => {
        let done = false;
        const finish = (ok) => {
          if (done) return;
          done = true;
          standby.removeEventListener('timeupdate', onTimeUpdate);
          resolve(ok);
        };
        const onTimeUpdate = () => finish(true);
        standby.addEventListener('timeupdate', onTimeUpdate);
        if (!standby.paused && standby.currentTime > 0) finish(true);
        setTimeout(() => finish(!standby.paused), 4000);
      });
      if (gen !== this._swapGen) {
        standby.pause();
        return 'superseded';
      }
      if (!started) {
        standby.pause();
        console.info('[FX] seamless fallback: standby clock never advanced');
        return 'fallback';
      }

      // One corrective seek to the live position, waiting for it to land
      await new Promise((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          standby.removeEventListener('seeked', finish);
          resolve();
        };
        standby.addEventListener('seeked', finish);
        try {
          standby.currentTime = Math.max(0, (active.currentTime || 0) + SWITCH_LEAD_SEC);
        } catch (e) {
          finish();
        }
        setTimeout(finish, 500);
      });
      if (gen !== this._swapGen) {
        standby.pause();
        return 'superseded';
      }

      // The flip: unmute the new stream, briefly overlap, silence the old.
      standby.muted = false;
      await new Promise((resolve) => setTimeout(resolve, OVERLAP_MS));

      if (standby.paused) {
        // iOS paused it on unmute (element wasn't blessed) — undo and fall back
        standby.muted = true;
        console.info('[FX] seamless fallback: iOS paused standby on unmute');
        return 'fallback';
      }

      active.pause();

      // Swap element roles and finish bookkeeping
      const previousSwapUrl = this._activeSwapBlobUrl;
      this._swapElementRoles();
      this.isPlaying = true;
      this._reductionEnvelope = reductionEnvelope;
      this._activeSwapBlobUrl = isBlob ? newSrc : null;
      if (previousSwapUrl && previousSwapUrl !== newSrc) {
        URL.revokeObjectURL(previousSwapUrl);
      }
      this._syncPlaybackState();
      console.info('[FX] seamless switch complete');

      // iOS may pause the element shortly AFTER an un-blessed unmute slips
      // through the paused-check — verify the flip actually took.
      this._verifyFlipTook(this.audio, this._swapGen);

      return 'done';
    } catch (error) {
      console.error('Seamless FX switch failed; falling back to instant swap:', error);
      try {
        standby.pause();
        standby.muted = true;
      } catch (e) {
        // ignore
      }
      return 'fallback';
    }
  }

  /**
   * Post-flip watchdog: if iOS quietly pauses the freshly-unmuted element
   * shortly after a seamless switch, try to resume it once; if that is
   * refused, redo the switch via the robust instant path so the user gets
   * FX'd audio (with a small gap) rather than silence.
   * @param {HTMLAudioElement} element - the element that should be playing
   * @param {number} gen - swap generation at flip time
   */
  _verifyFlipTook(element, gen) {
    setTimeout(() => {
      if (gen !== this._swapGen || this.audio !== element || !this.isPlaying) {
        return; // superseded by a track change or another swap
      }
      if (!element.paused) {
        return; // all good
      }
      console.info('[FX] flip verification: element paused post-flip — resuming');
      const p = element.play();
      if (p && typeof p.then === 'function') {
        p.catch(() => {
          console.info('[FX] flip verification: resume refused — element likely unblessed');
        });
      }
    }, this._flipVerifyDelayMs);
  }

  /**
   * Make the standby element the active one (and vice versa), migrating the
   * listeners that AudioService attaches per-element.
   */
  _swapElementRoles() {
    const oldActive = this.audio;
    const newActive = this._standbyAudio;

    if (this._wrappedEndedCallback) {
      oldActive.removeEventListener('ended', this._wrappedEndedCallback);
      newActive.addEventListener('ended', this._wrappedEndedCallback);
    }
    if (this._boundTimeupdateHandler) {
      oldActive.removeEventListener('timeupdate', this._boundTimeupdateHandler);
      newActive.addEventListener('timeupdate', this._boundTimeupdateHandler);
    }

    // Reset the retired element for future standby duty
    oldActive.muted = false;

    this.audio = newActive;
    this._standbyAudio = oldActive;

    // The button now reflects the new active element
    this._bindElementStateListeners(newActive);
  }

  /**
   * iOS: swap back to the clean (unprocessed) source after all FX were
   * turned off. Uses cached bytes when available (instant, no network).
   */
  async _revertToOriginalSource() {
    if (!this.audio || !this._currentTrackUrl) {
      return;
    }
    if (!this._activeSwapBlobUrl) {
      return; // already playing the clean source
    }

    const originalBlobUrl = OfflineFxService.getOriginalBlobUrl(this._currentTrackUrl);
    await this._swapToSource(originalBlobUrl || this._currentTrackUrl);
  }

  /**
   * Bookkeeping when the element is about to get a brand-new track source:
   * cancel pending renders, invalidate caches, and queue the old swap blob
   * for revocation once the new source has loaded (revoking the blob that
   * is still the element's current src would be unsafe).
   * @param {string} url - The new track's original URL
   */
  _onTrackSourceChanging(url) {
    this._currentTrackUrl = url;
    this._cancelPendingOfflineRender();
    this._reductionEnvelope = null;
    this._swapGen++; // abort any in-flight seamless switch
    if (this._queuedSwap) {
      if (this._queuedSwap.isBlob) {
        URL.revokeObjectURL(this._queuedSwap.newSrc);
      }
      this._queuedSwap = null;
    }
    OfflineFxService.invalidate();

    // Release queued blobs from the outgoing track, and queue its swap blob
    this._flushPendingRevoke();
    if (this._activeSwapBlobUrl) {
      this._queuePendingRevoke(this._activeSwapBlobUrl);
      this._activeSwapBlobUrl = null;
    }
  }

  _queuePendingRevoke(blobUrl) {
    if (blobUrl && this._pendingRevokeUrls.indexOf(blobUrl) === -1) {
      this._pendingRevokeUrls.push(blobUrl);
    }
  }

  /**
   * Revoke queued blob URLs — except one that is still the element's
   * current src (it stays queued until the next source change).
   */
  _flushPendingRevoke() {
    if (!this._pendingRevokeUrls.length) return;
    const currentSrc = this.audio ? this.audio.src : null;
    const keep = [];
    for (const blobUrl of this._pendingRevokeUrls) {
      if (currentSrc && blobUrl === currentSrc) {
        keep.push(blobUrl);
      } else {
        URL.revokeObjectURL(blobUrl);
      }
    }
    this._pendingRevokeUrls = keep;
  }

  /** Unconditional release of every queued blob (teardown only). */
  _flushPendingRevokeAll() {
    for (const blobUrl of this._pendingRevokeUrls) {
      URL.revokeObjectURL(blobUrl);
    }
    this._pendingRevokeUrls = [];
  }

  /**
   * Reset the audio service state
   */
  reset() {
    // Reset Web Audio API first
    this.resetWebAudio();

    // Clean up timeupdate end detection
    if (this._nearEndTimeout) {
      clearTimeout(this._nearEndTimeout);
      this._nearEndTimeout = null;
    }
    this._endDetectionFired = false;

    if (this._endedSelfHealTimer) {
      clearTimeout(this._endedSelfHealTimer);
      this._endedSelfHealTimer = null;
    }

    if (this.audio) {
      this.pause();
      // Remove listeners before destroying the element
      if (this._wrappedEndedCallback) {
        this.audio.removeEventListener("ended", this._wrappedEndedCallback);
      }
      if (this._boundTimeupdateHandler) {
        this.audio.removeEventListener("timeupdate", this._boundTimeupdateHandler);
      }
      this._unbindElementStateListeners();
      this.audio.src = ""; // Release network connection
      this.audio = null;
    }
    this._wrappedEndedCallback = null;
    this._boundTimeupdateHandler = null;
    this._transitionDepth = 0;

    // Clean up prefetch state
    if (this._prefetchAbortController) {
      this._prefetchAbortController.abort();
      this._prefetchAbortController = null;
    }
    if (this._prefetchedBlobUrl) {
      URL.revokeObjectURL(this._prefetchedBlobUrl);
      this._prefetchedBlobUrl = null;
      this._prefetchedUrl = null;
    }

    // Clean up hybrid routing state (offline renders / swap blobs)
    this._cancelPendingOfflineRender();
    this._reductionEnvelope = null;
    this._swapGen++;
    OfflineFxService.invalidate();
    if (this._activeSwapBlobUrl) {
      URL.revokeObjectURL(this._activeSwapBlobUrl);
      this._activeSwapBlobUrl = null;
    }
    if (this._queuedSwap) {
      if (this._queuedSwap.isBlob) {
        URL.revokeObjectURL(this._queuedSwap.newSrc);
      }
      this._queuedSwap = null;
    }
    if (this._standbyAudio) {
      try {
        this._standbyAudio.pause();
      } catch (e) {
        // ignore
      }
    }
    this._disarmPlaybackConfirmation();
    this._flushPendingRevokeAll();
    this._currentTrackUrl = null;
    this._swapInProgress = false;
    this._hasUserInitiatedPlayback = false;
    this._fxTooLongNotifiedUrl = null;

    this.isPlaying = false;
    this.isLoading = true;
    this.trackReloadCounter = 0;
    this.sameTrackPositionCounter = 0;
    this.previousTrackPosition = "00:00";
    this._cleanupSeekState();
    this._syncPlaybackState();
  }
}

// Memory guards (class constants; kept off the prototype for easy tuning)
// Prefetch only ever requests MP3s (see player.js prefetchNextTrack) — the
// longest catalog mp3s run ~30 MB, so 80 MB is a pure safety ceiling against
// absurd/misconfigured files, NOT a cap that any real track should hit.
// (An earlier 25 MB cap silently skipped prefetch for long tracks, which
// broke locked-screen auto-advance into them on iOS.)
AudioService.PREFETCH_MAX_BYTES = 80 * 1024 * 1024;
AudioService.OFFLINE_FX_MAX_SECONDS = 12 * 60; // no offline FX render beyond 12 minutes
// Locked-screen iOS: advance this many seconds BEFORE the track ends, while
// the audio pipeline is still hot (after 'ended' the page may be suspended
// or play() left pending forever). Foreground advance stays on 'ended'.
AudioService.PRE_END_ADVANCE_SECONDS = 0.45;
// How long a fire-and-forget play() may stay unconfirmed before the state
// flags self-heal to "paused" (timer freezes with the page; fires on wake).
AudioService.PLAY_CONFIRM_TIMEOUT_MS = 4000;
// HTMLMediaElement.HAVE_FUTURE_DATA: the element has enough data to actually
// advance — the line between "loading" and "playing" for the button.
AudioService.HAVE_FUTURE_DATA = 3;
// A 'pause' this close to the end of the track is the browser stopping at the
// end (it fires 'pause' right before 'ended'), not an interruption.
AudioService.END_OF_TRACK_EPSILON_SECONDS = 0.25;
// After 'ended', how long the next track (or an explicit stop) has to take
// over before a still-paused, still-ended element is treated as stopped.
AudioService.ENDED_SELF_HEAL_MS = 1500;

const audioServiceInstance = new AudioService();

// Dev/test-only introspection hooks (absent in production builds):
// - e2e specs assert on playback state through window.__audioService
// - in the iOS Simulator, type __audioService in the Web Inspector console
//   to inspect the live element, swap state, and GR envelope
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.__audioService = audioServiceInstance;
  window.__pcpStore = store;
}

export default audioServiceInstance;